import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { GridSearch } from '../../../components/GridSearch';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDate } from '../../../utils/datetime';
import { apiMessage, fetchTestOrderCandidates } from './api';
import type { SuboutSampleDetailRow } from './types';

// Subcontract Test Order → Subout Samples → Add (G5 SuboutAddSample, popup
// SampleParameter_ListView_Copy_SubOutAddSamples, 1210 × 700). Lists every pending subout
// test plus any test removed from this order but not yet saved, minus the tests already in
// the grid. Picking only stages the rows — nothing is written until the order is saved.

const keyOf = (r: { sampleOid: string; testMethodOid: string }) => `${r.sampleOid}:${r.testMethodOid}`;

const COLUMNS: AppColumn<SuboutSampleDetailRow>[] = [
  { field: 'jobId',         header: 'JobID',         width: 110, text: (r) => r.jobId ?? '' },
  { field: 'sampleId',      header: 'SampleID',      width: 130, text: (r) => r.sampleId ?? '' },
  { field: 'matrix',        header: 'Matrix',        width: 110, text: (r) => r.matrix ?? '' },
  { field: 'test',          header: 'Test',          width: 160, text: (r) => r.test ?? '' },
  { field: 'method',        header: 'Method',        width: 120, text: (r) => r.method ?? '' },
  { field: 'parameter',     header: 'Parameter',     width: 200, text: (r) => r.parameter ?? '' },
  { field: 'dateCollected', header: 'DateCollected', width: 120, type: 'date', text: (r) => fmtLocalDate(r.dateCollected) },
  { field: 'client',        header: 'Client',        width: 200, text: (r) => r.client ?? '' },
];

interface Props {
  orderOid: string;
  /** Tests currently in the grid (never offered again). */
  inGrid: SuboutSampleDetailRow[];
  /** Tests removed from this order but not saved yet (G5 offers them back). */
  removed: SuboutSampleDetailRow[];
  onClose: () => void;
  onAdd: (rows: SuboutSampleDetailRow[]) => void;
}

export default function SuboutAddSamplesDialog({ orderOid, inGrid, removed, onClose, onAdd }: Props) {
  const notify = useNotify();
  const [rows, setRows] = useState<SuboutSampleDetailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  useEffect(() => {
    let live = true;
    fetchTestOrderCandidates(orderOid)
      .then((c) => {
        if (!live) return;
        const taken = new Set(inGrid.map(keyOf));
        const all = [...removed, ...c];
        const seen = new Set<string>();
        setRows(all.filter((r) => {
          const k = keyOf(r);
          if (taken.has(k) || seen.has(k)) return false;
          seen.add(k);
          return true;
        }));
      })
      .catch((e) => notify.error(apiMessage(e, 'Failed to load the pending subout samples.')))
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [orderOid, inGrid, removed, notify]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.jobId, r.sampleId, r.test, r.method, r.parameter, r.client]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  function ok() {
    const picked = rows.filter((r) => selected.has(keyOf(r)));
    // G5 messages (Model.xafml selectchkbox / additem).
    if (picked.length === 0) { notify.warning('Please select an item to proceed further.'); return; }
    onAdd(picked);
    notify.success(picked.length === 1 ? 'The selected item was added.' : 'The selected items were added.');
  }

  return (
    <AppDialog
      open
      title="Add Subout Samples"
      icon="ti-plus"
      width={1100}
      maximizable
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={ok}>OK</button>
        </>
      )}
    >
      <div style={{ marginBottom: 8 }}>
        <GridSearch value={search} onChange={setSearch} placeholder="Search job, sample, test, client…" />
      </div>
      <AppDataGrid<SuboutSampleDetailRow>
        rows={visible}
        columns={COLUMNS}
        getRowId={keyOf}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        defaultPageSize={10}
        emptyText="No pending subout samples."
      />
    </AppDialog>
  );
}
